import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Badge,
  Box,
  Button,
  Card,
  CardBody,
  Flex,
  Heading,
  Select,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { useCallback, useEffect, useRef, useState } from "react";
import denunciaService from "../Landing/denunciaService";
import ModernPageShell from "../../components/layout/ModernPageShell";
import { getModernUi } from "../../components/layout/modernUi";

const STATUS_LABEL = {
  PENDENTE: "Pendente",
  EM_ANALISE: "Em análise",
  CONCLUIDA: "Concluída",
};

const STATUS_COLOR = {
  PENDENTE: "orange",
  EM_ANALISE: "blue",
  CONCLUIDA: "green",
};

function formatDate(value) {
  if (!value) return "-";
  return new Date(value).toLocaleString("pt-BR");
}

export default function Denuncias({ modern = false }) {
  const ui = getModernUi(modern);
  const toast = useToast();
  const [denuncias, setDenuncias] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [denunciaToDelete, setDenunciaToDelete] = useState(null);
  const {
    isOpen: isDeleteDialogOpen,
    onOpen: onDeleteDialogOpen,
    onClose: onDeleteDialogClose,
  } = useDisclosure();
  const cancelDeleteRef = useRef();

  const loadDenuncias = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await denunciaService.findAll();
      setDenuncias(Array.isArray(data) ? data : []);
    } catch (error) {
      toast({
        title: "Erro ao carregar denúncias",
        description:
          error.response?.data?.message ||
          "Não foi possível buscar as denúncias.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    loadDenuncias();
  }, [loadDenuncias]);

  const handleStatusChange = async (id, status) => {
    setUpdatingId(id);
    try {
      const updated = await denunciaService.updateStatus(id, status);
      setDenuncias((prev) =>
        prev.map((item) => (item.denunciaId === id ? updated : item))
      );
      toast({
        title: "Status atualizado",
        status: "success",
        duration: 2500,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: "Erro ao atualizar status",
        description:
          error.response?.data?.message ||
          "Não foi possível atualizar o status.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setUpdatingId(null);
    }
  };

  const handleDeleteClick = (item) => {
    setDenunciaToDelete(item);
    onDeleteDialogOpen();
  };

  const handleConfirmDelete = async () => {
    if (!denunciaToDelete) return;

    setDeletingId(denunciaToDelete.denunciaId);
    try {
      await denunciaService.delete(denunciaToDelete.denunciaId);
      setDenuncias((prev) =>
        prev.filter((item) => item.denunciaId !== denunciaToDelete.denunciaId)
      );
      toast({
        title: "Denúncia excluída",
        status: "success",
        duration: 2500,
        isClosable: true,
      });
      onDeleteDialogClose();
      setDenunciaToDelete(null);
    } catch (error) {
      toast({
        title: "Erro ao excluir denúncia",
        description:
          error.response?.data?.message ||
          "Não foi possível excluir a denúncia.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setDeletingId(null);
    }
  };

  const pageContent = (
    <>
      <Flex justify="space-between" align="center" mb={6}>
        {!modern && <Heading size="lg">Denúncias</Heading>}
        <Text color="gray.600">{denuncias.length} registro(s)</Text>
      </Flex>

      <Card {...ui.panel}>
        <CardBody>
          {isLoading ? (
            <Flex justify="center" py={10}>
              <Spinner />
            </Flex>
          ) : denuncias.length === 0 ? (
            <Text color="gray.500">Nenhuma denúncia registrada.</Text>
          ) : (
            <Box overflowX="auto">
              <Table variant="simple" size="sm">
                <Thead>
                  <Tr>
                    <Th>Data</Th>
                    <Th>Título</Th>
                    <Th>Categoria</Th>
                    <Th>Denunciante</Th>
                    <Th>E-mail</Th>
                    <Th>Status</Th>
                    <Th>Descrição</Th>
                    <Th>Ações</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {denuncias.map((item) => (
                    <Tr key={item.denunciaId}>
                      <Td whiteSpace="nowrap">
                        {formatDate(item.registerDate)}
                      </Td>
                      <Td fontWeight="medium">{item.titulo}</Td>
                      <Td>{item.categoria || "-"}</Td>
                      <Td>
                        {item.anonima ? (
                          <Badge>Anônima</Badge>
                        ) : (
                          <Text>{item.nomeDenunciante || "-"}</Text>
                        )}
                      </Td>
                      <Td>
                        {item.contatoDenunciante ? (
                          <Text as="a" href={`mailto:${item.contatoDenunciante}`} color="blue.600">
                            {item.contatoDenunciante}
                          </Text>
                        ) : (
                          "-"
                        )}
                      </Td>
                      <Td minW="170px">
                        <Flex direction="column" gap={2}>
                          <Badge
                            colorScheme={STATUS_COLOR[item.status] || "gray"}
                            w="fit-content"
                          >
                            {STATUS_LABEL[item.status] || item.status}
                          </Badge>
                          <Select
                            size="sm"
                            value={item.status}
                            isDisabled={updatingId === item.denunciaId}
                            onChange={(event) =>
                              handleStatusChange(
                                item.denunciaId,
                                event.target.value
                              )
                            }
                          >
                            <option value="PENDENTE">Pendente</option>
                            <option value="EM_ANALISE">Em análise</option>
                            <option value="CONCLUIDA">Concluída</option>
                          </Select>
                        </Flex>
                      </Td>
                      <Td maxW="320px">
                        <Text noOfLines={3}>{item.descricao}</Text>
                      </Td>
                      <Td whiteSpace="nowrap">
                        <Button
                          size="sm"
                          colorScheme="red"
                          variant="outline"
                          onClick={() => handleDeleteClick(item)}
                          isDisabled={
                            deletingId === item.denunciaId ||
                            updatingId === item.denunciaId
                          }
                          isLoading={deletingId === item.denunciaId}
                        >
                          Excluir
                        </Button>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          )}
        </CardBody>
      </Card>

      <AlertDialog
        isOpen={isDeleteDialogOpen}
        leastDestructiveRef={cancelDeleteRef}
        onClose={onDeleteDialogClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Excluir denúncia
            </AlertDialogHeader>
            <AlertDialogBody>
              Tem certeza que deseja excluir a denúncia{" "}
              <strong>{denunciaToDelete?.titulo}</strong>? Essa ação não pode
              ser desfeita.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button
                ref={cancelDeleteRef}
                onClick={onDeleteDialogClose}
                isDisabled={!!deletingId}
              >
                Cancelar
              </Button>
              <Button
                colorScheme="red"
                onClick={handleConfirmDelete}
                ml={3}
                isLoading={!!deletingId}
              >
                Excluir
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );

  if (modern) {
    return (
      <ModernPageShell
        title="Denúncias"
        subtitle="Acompanhe e gerencie os relatos recebidos pelo canal público."
        eyebrow="Compliance"
      >
        {pageContent}
      </ModernPageShell>
    );
  }

  return <Box p={6}>{pageContent}</Box>;
}
